import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from "react-helmet-async";
import { FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import '../styles/MaharashtraPage.css';
import ClientBranches from './ClientBranches';
import CompanyProfile1 from './CompanyProfile1';

const MaharashtraClientsPage = () => {
  const [visibleCards, setVisibleCards] = useState(0);
  const [counters, setCounters] = useState({
    clients: 0,
    peopleServed: 0,
    locations: 0
  });

  // Refs for hero section text animations
  const mcpHeroTitleRef = useRef(null);
  const mcpHeroBreadcrumbRef = useRef(null);

  // Maharashtra specific client data
  const maharashtraClients = [
    { name: "INDO COUNT INDUSTRIES LTD", location: "Kolhapur", state: "Maharashtra", persons: 650, logo: "/newlyaddedclients/indocount.png", industry : "Manufacturing" },
    { name: "THE SUPREME INDUSTRIES LTD", location: "Sangli", state: "Maharashtra", persons: 200, logo: "/newlyaddedclients/supreme.png", industry : "Textiles" },
    { name: "PRESICE CHEMI PHARMA LTD.", location: "Dindori", persons: 400, logo: "/clients/Precise-chemi-2.png", industry: "Pharmaceutical" },
    { name: "SUZLON ENERGY LTD. CHADVEL NANDURBAR", location: "Nandurbar", persons: 1500, logo: "/clients/Suzlon-3.png", industry: "Energy" },
    { name: "EVEREST INDIA INDUSTRIES PVT. LTD.", location: "Dindori", persons: 700, logo: "/clients/Everest-industries-4.png", industry: "Industrial" },
    // { name: "KOLHAPUR STEEL (KIRLOSKAR GROUPS)", location: "Kolhapur", persons: 400, logo: "/clients/TKSL-kolhapur-stell-6.png", industry: "Steel" },
    { name: "MENON AND MENON LTD.", location: "Kagal", persons: 600, logo: "/clients/Menon-and-menon-8.png", industry: "Industrial" },
    { name: "MENON AND MENON LTD.", location: "Vikramnagar", persons: 1000, logo: "/clients/Menon-and-menon-8.png", industry: "Industrial" },
    { name: "KIRLOSKAR BROTHERS LTD.", location: "Sangli", persons: 400, logo: "/clients/Kirloskar-brothers-9.png", industry: "Industrial" },
    // { name: "GHATGE PATIL INDUSTRIES LTD.", location: "Kolhapur", persons: 800, logo: "/clients/Ghatge-patil-10.png", industry: "Industrial" },
    { name: "EBIXCASH GLOBAL SERVICES PVT LTD.", location: "Mumbai", persons: 600, logo: "/clients/ebix-cash-global-14.png", industry: "Technology" },
    { name: " MANTRI METALLICS PVT LTD", location: "Mumbai, Kagal - MIDC", persons: 500, logo: "/newlyaddedclients/Mantri-metallics.png", industry: "Manufacturing" },
    { name: " MANTRI Auto Components PVT. LTD", location: "Mumbai, Kagal - MIDC", persons: 200, logo: "/newlyaddedclients/Mantri-auto.png", industry: "Automotive and Manufacturing" } , 
    { name: "KINET RAILWAY SOLUTION LTD ", location: "Latur, Maharashtra", persons: 1500, logo: "/newlyaddedclients/Kinet-Railway-Solutions-1.png", industry: "Railway Engineering and Manufacturing" }
  ];

  // Calculate total people served and unique locations
  const totalPeopleServed = maharashtraClients.reduce((sum, client) => sum + client.persons, 0);
  const totalLocations = [...new Set(maharashtraClients.map(client => client.location))].length;

  // Animate counters on component mount
  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          clients: Math.floor(maharashtraClients.length * progress),
          peopleServed: Math.floor(totalPeopleServed * progress),
          locations: Math.floor(totalLocations * progress)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCounters({
            clients: maharashtraClients.length,
            peopleServed: totalPeopleServed,
            locations: totalLocations
          });
        }
      }, stepTime);
    };
    animateCounters();
  }, [maharashtraClients.length, totalPeopleServed, totalLocations]);

  // Animate client cards appearing in a staggered manner
  useEffect(() => {
    const timer = setTimeout(() => {
      if (visibleCards < maharashtraClients.length) {
        setVisibleCards(prev => prev + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [visibleCards, maharashtraClients.length]);

  // Callback for Intersection Observer to add/remove animation class
  const handleIntersect = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("mcp-animate-in");
      } else {
        entry.target.classList.remove("mcp-animate-in");
      }
    });
  }, []);

  // Set up Intersection Observers for hero text elements
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };
    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    const currentHeroTitleRef = mcpHeroTitleRef.current;
    const currentHeroBreadcrumbRef = mcpHeroBreadcrumbRef.current;

    if (currentHeroTitleRef) observer.observe(currentHeroTitleRef);
    if (currentHeroBreadcrumbRef) observer.observe(currentHeroBreadcrumbRef);

    return () => {
      if (currentHeroTitleRef) observer.unobserve(currentHeroTitleRef);
      if (currentHeroBreadcrumbRef) observer.unobserve(currentHeroBreadcrumbRef);
    };
  }, [handleIntersect]);

  return (
    <>
     <Helmet>
       <title>Clients in Maharashtra | Trusted Industrial Partners | Laxmi Hospitality Services</title>
       <meta
         name="description"
         content="Discover Laxmi Hospitality Services' trusted client partners across Maharashtra. Serving diverse industries like Pharmaceuticals, Energy, Industrial, and Technology with custom catering solutions that empower thousands daily."
       />
       <meta name="robots" content="index, follow" />
       <meta name="viewport" content="width=device-width, initial-scale=1.0" />
       <link rel="canonical" href="https://laxmihospitalityservices.in/clients/maharashtra" />
     
       {/* -------- OPEN GRAPH (SOCIAL) -------- */}
       <meta property="og:type" content="website" />
       <meta property="og:url" content="https://laxmihospitalityservices.in/clients/maharashtra" />
       <meta property="og:title" content="Clients in Maharashtra | Trusted Industrial Partners | Laxmi Hospitality Services" />
       <meta property="og:description" content="Discover our trusted client partners across Maharashtra. Serving 11 leading companies in 7 locations with 6,850+ people served daily." />
       <meta property="og:site_name" content="Laxmi Hospitality Services" />
       <meta property="og:locale" content="en_IN" />
       <meta property="og:image" content="https://laxmihospitalityservices.in/images/Mumbai-hero-image.png" />
     
       {/* -------- MAHARASHTRA CLIENTS COLLECTION PAGE -------- */}
       <script type="application/ld+json">
         {`
           {
             "@context": "https://schema.org",
             "@type": "CollectionPage",
             "@id": "https://laxmihospitalityservices.in/clients/maharashtra",
             "name": "Maharashtra Clients - Laxmi Hospitality Services",
             "description": "Industrial catering clients across Maharashtra state - serving leading companies in pharmaceuticals, energy, manufacturing, and technology sectors.",
             "url": "https://laxmihospitalityservices.in/clients/maharashtra",
             "about": { "@id": "https://laxmihospitalityservices.in/#organization" },
             "publisher": { "@id": "https://laxmihospitalityservices.in/#organization" },
             "isPartOf": {
               "@type": "WebPage",
               "@id": "https://laxmihospitalityservices.in/clients",
               "name": "Our Clients Across India"
             },
             "mainEntity": {
               "@type": "ItemList",
               "name": "Maharashtra Industrial Clients",
               "description": "Leading industrial companies served by Laxmi Hospitality Services across Maharashtra",
               "numberOfItems": 9,
               "itemListElement": [
                 {
                   "@type": "Organization",
                   "name": "Suzlon Energy Ltd.",
                   "description": "Renewable energy leader in Nandurbar serving 1,500+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Nandurbar",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Menon and Menon Ltd.",
                   "description": "Industrial manufacturing company in Vikramnagar and Kagal serving 1,600+ people combined",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Vikramnagar",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Ghatge Patil Industries Ltd.",
                   "description": "Industrial leader in Kolhapur serving 800+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Kolhapur",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Everest India Industries Pvt. Ltd.",
                   "description": "Industrial manufacturing company in Dindori serving 700+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Dindori",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Indo Count Industries Ltd.",
                   "description": "Leading textile manufacturer in Kolhapur serving 650+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Kolhapur",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Ebixcash Global Services Pvt. Ltd.",
                   "description": "Technology services company in Mumbai serving 600+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Mumbai",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Precise Chemi Pharma Ltd.",
                   "description": "Pharmaceutical manufacturing partner in Dindori serving 400+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Dindori",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Kirloskar Brothers Ltd.",
                   "description": "Industrial engineering leader in Sangli serving 400+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Sangli",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "The Supreme Industries Ltd.",
                   "description": "Textiles innovator in Sangli serving 200+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Sangli",
                     "addressRegion": "Maharashtra",
                     "addressCountry": "IN"
                   }
                 }
               ]
             },
             "sameAs": [
               "https://www.linkedin.com/company/laxmi-hospitality-services/",
               "https://www.instagram.com/laxmi_hospitality_services/"
             ],
             "inLanguage": "en-IN"
           }
         `}
       </script>
     
       {/* -------- STATE-SPECIFIC BREADCRUMB -------- */}
       <script type="application/ld+json">
         {`
           {
             "@context": "https://schema.org",
             "@type": "BreadcrumbList",
             "@id": "https://laxmihospitalityservices.in/#breadcrumb",
             "name": "Website Breadcrumb",
             "itemListElement": [
               { "@type": "ListItem", "position": 1, "name": "Home",      "item": "https://laxmihospitalityservices.in/" },
               { "@type": "ListItem", "position": 2, "name": "Clients",   "item": "https://laxmihospitalityservices.in/clients" },
               { "@type": "ListItem", "position": 3, "name": "Maharashtra", "item": "https://laxmihospitalityservices.in/clients/maharashtra" }
             ]
           }
         `}
       </script>
     </Helmet>
     
    <div className="mcp-page-container">
      {/* Hero Section */}
      <section className="mcp-hero-section">
        <img
          src="/images/Mumbai-hero-image.png"
          alt="Maharashtra Industry Landscape"
          className="mcp-hero-image"
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1920x600/f0f0f0/333333?text=Maharashtra+Hero"; }}
        />
        <div className="mcp-hero-overlay">
          <h2 ref={mcpHeroTitleRef} className="mcp-hero-title">Our Clients in Maharashtra</h2>
          <p ref={mcpHeroBreadcrumbRef} className="mcp-breadcrumb">
            <a href="/" className="mcp-breadcrumb-link">Home</a> /
            <a href="/clients" className="mcp-breadcrumb-link"> Clients</a> / Maharashtra
          </p>
        </div>
      </section>

      {/* Introductory Sentence for Stats Section */}
      <section className="mcp-intro-stats-section">
        <div className="mcp-container">
          <p className="mcp-intro-stats-text">
            Our extensive network across Maharashtra reflects a deep commitment to serving diverse industries and empowering thousands of individuals through our partnerships.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="mcp-stats-section">
        <div className="mcp-container">
          <div className="mcp-stats-grid">
            <div className="mcp-stat-card">
              <div className="mcp-stat-number">{counters.clients}</div>
              <div className="mcp-stat-label">Clients</div>
            </div>
            <div className="mcp-stat-card">
              <div className="mcp-stat-number">{counters.peopleServed.toLocaleString()}</div>
              <div className="mcp-stat-label">People Serving</div>
            </div>
            <div className="mcp-stat-card">
              <div className="mcp-stat-number">{counters.locations}</div>
              <div className="mcp-stat-label">Locations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="mcp-clients-section">
        <div className="mcp-container">
          <h2 className="mcp-clients-section-title">Our Valued Clients</h2>
          <p className="mcp-clients-section-intro">
            Discover the leading organizations in Maharashtra that trust our services.
            We are proud to partner with these industry leaders, contributing to their success
            across various sectors.
          </p>
          <div className="mcp-clients-grid">
            {maharashtraClients
              .slice()
              .sort((a, b) => b.persons - a.persons)
              .map((client, index) => (
              <div
                key={index}
                className={`mcp-client-card ${index < visibleCards ? 'mcp-visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Client Logo at the Top */}
                <div className="mcp-card-logo-wrapper">
                  <img
                    src={client.logo}
                    alt={`${client.name} Logo`}
                    className="mcp-card-logo-image"
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/150x100/e0e0e0/333333?text=${client.name.split(' ')[0]}`; }}
                  />
                </div>

                {/* Client Info below logo */}
                <div className="mcp-client-info-v2">
                  <h3 className="mcp-client-name-v2">{client.name}</h3>
                  <div className="mcp-location-v2">
                    <FaMapMarkerAlt className="mcp-icon-v2" />
                    {client.location}
                  </div>
                  <div className="mcp-people-served">
                    <FaUsers className="mcp-icon-v2" />
                    {client.persons.toLocaleString()} People Serving
                  </div>
                  <div className="mcp-type-tag">
                    Type: {client.industry}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
     <ClientBranches/>
    <CompanyProfile1/>
    </div>
    </>
  );
};

export default MaharashtraClientsPage;
